import { SaleInfoResponse } from "../../../infrastructure/model/SaleInfoModel";
import { OrderItem } from "../../../presentations/context/OrderListContext";
import { ALL_ITEM_ID, SALE_TYPE_ENUM, SALE_VALUE_TYPE_ENUM } from "../../constants/AppConst";
import { getTodayValue } from "../../utils/TimeUtils";

interface OrderItemWithFinalPrice extends OrderItem {
  finalPrice: number;
  appliedSale?: SaleInfoResponse;
}

export interface SaleWithDiscount {
  sale: SaleInfoResponse;
  discountAmount: number;
  discountPercent: number;
}

export interface OrderCalculationResult {
  items: OrderItemWithFinalPrice[];
  originalTotal: number;
  discountedTotal: number;
  totalDiscount: number;
  appliedOrderSale?: SaleInfoResponse[];
  appliedAllSales?: SaleInfoResponse[];
  bestSale?: SaleWithDiscount; // Sale có discount lớn nhất
  allSalesWithDiscount?: SaleWithDiscount[]; // Tất cả sales đã áp dụng với discount của từng sale
}

export interface CalculateOrderOptions {
  useNewUserSale?: boolean;
  totalOrders?: number;
}

// ============ HELPER FUNCTIONS ============

const isSaleActiveToday = (sale: SaleInfoResponse, now: number): boolean => {
  if (!sale.is_recurring) {
    return now >= sale.sale_start_date && now <= sale.sale_end_date;
  }

  if (!sale.recurring_days?.length) return false;

  const today = getTodayValue();
  return sale.recurring_days.includes(today);
};

const isValidTotalOrderCondition = (sale: SaleInfoResponse, totalOrders: number): boolean => {
  const { minimum_orders_required: min, max_orders_required: max } = sale;

  if (min != null && max != null) return totalOrders >= min && totalOrders < max;
  if (min != null) return totalOrders >= min;
  if (max != null) return totalOrders < max;

  return true;
};

const filterActiveSales = (
  saleActive: SaleInfoResponse[],
  saleType: SALE_TYPE_ENUM,
  now: number,
  totalOrders?: number
): SaleInfoResponse[] => {
  return saleActive.filter(
    (sale) =>
      sale.is_active &&
      sale.auto_apply_for_user &&
      sale.sale_type === saleType &&
      isSaleActiveToday(sale, now) &&
      (saleType !== SALE_TYPE_ENUM.NEW_USER || isValidTotalOrderCondition(sale, totalOrders || 0))
  );
};

const calculateBasePrice = (item: OrderItem): number => {
  const toppingTotal = (item.moreTopping ?? []).reduce(
    (sum, topping) => (topping.can_sale ? sum + topping.price : sum),
    0
  );
  return item.price + toppingTotal;
};

const buildQuantityMap = (orderList: OrderItem[]): Map<string, number> => {
  const map = new Map<string, number>();
  for (const item of orderList) {
    const key = `${item.id}_${item.size}`;
    map.set(key, (map.get(key) ?? 0) + item.quantity);
  }
  return map;
};

// ============ SALE APPLICATION FUNCTIONS ============

interface SaleScenario {
  items: OrderItemWithFinalPrice[];
  discountedTotal: number;
  appliedSales: SaleInfoResponse[];
  totalDiscount: number;
  salesWithDiscount: SaleWithDiscount[];
}

// const applyProductSales = (
//   orderList: OrderItem[],
//   productSales: SaleInfoResponse[],
//   quantityMap: Map<string, number>
// ): { items: OrderItemWithFinalPrice[]; baseTotal: number } => {
//   let baseTotal = 0;

//   const items: OrderItemWithFinalPrice[] = orderList.map(item => {
//     const basePrice = calculateBasePrice(item);
//     const key = `${item.id}_${item.size}`;
//     const totalGroupedQty = quantityMap.get(key) ?? item.quantity;

//     const applicableSale = productSales.find(sale =>
//       (sale.apply_for?.includes(ALL_ITEM_ID) || sale.apply_for?.includes(item.id)) &&
//       (!sale.apply_for_size || sale.apply_for_size.includes(item.size)) &&
//       (!sale.sale_order_condition || totalGroupedQty >= sale.sale_order_condition) &&
//       sale.sale_value_type !== SALE_VALUE_TYPE_ENUM.CUP
//     );

//     let finalPrice = basePrice;

//     if (applicableSale) {
//       const toppingTotal = (item.moreTopping ?? []).reduce(
//         (sum, t) => t.can_sale ? sum + t.price : sum, 0
//       );

//       switch (applicableSale.sale_value_type) {
//         case SALE_VALUE_TYPE_ENUM.VND:
//           finalPrice = applicableSale.sale_value
//             ? basePrice - applicableSale.sale_value
//             : applicableSale.new_price_temp ?? basePrice;
//           break;

//         case SALE_VALUE_TYPE_ENUM.PERCENT:
//           if (applicableSale.sale_value) {
//             finalPrice = basePrice * (1 - applicableSale.sale_value / 100);
//           }
//           break;

//         default:
//           if (applicableSale.new_price_temp != null && applicableSale.new_price_temp > 0) {
//             finalPrice = applicableSale.new_price_temp + toppingTotal;
//           }
//           break;
//       }

//       finalPrice = Math.max(finalPrice, 0);
//     }

//     baseTotal += finalPrice * item.quantity;

//     return {
//       ...item,
//       finalPrice,
//       appliedSale: applicableSale
//     };
//   });

//   return { items, baseTotal };
// };

// const applyProductSales = (
//   orderList: OrderItem[]
// ): { items: OrderItemWithFinalPrice[]; baseTotal: number } => {
//   let baseTotal = 0;

//   const items: OrderItemWithFinalPrice[] = orderList.map((item) => {
//     const basePrice = calculateBasePrice(item);
//     baseTotal += basePrice * item.quantity;

//     return {
//       ...item,
//       finalPrice: basePrice,
//       appliedSale: undefined,
//     };
//   });

//   return { items, baseTotal };
// };

const applyProductSales = (
  orderList: OrderItem[],
  productSales: SaleInfoResponse[],
  quantityMap: Map<string, number>
): { items: OrderItemWithFinalPrice[]; baseTotal: number } => {
  let baseTotal = 0;

  const items: OrderItemWithFinalPrice[] = orderList.map((item) => {
    const basePrice = calculateBasePrice(item);
    const key = `${item.id}_${item.size}`;
    const totalGroupedQty = quantityMap.get(key) ?? item.quantity;

    let bestFinalPrice = basePrice;
    let appliedSale: SaleInfoResponse | undefined = undefined;

    // Tìm tất cả sales sản phẩm phù hợp (không bao gồm loại CUP)
    const applicableSales = productSales.filter((sale) => {
      const isItemMatch =
        sale.apply_for?.includes(ALL_ITEM_ID) || sale.apply_for?.includes(item.id);
      const isSizeMatch = !sale.apply_for_size || sale.apply_for_size.includes(item.size);
      const isQtyMatch = !sale.sale_order_condition || totalGroupedQty >= sale.sale_order_condition;
      const isProductType = sale.sale_value_type !== SALE_VALUE_TYPE_ENUM.CUP;

      return isItemMatch && isSizeMatch && isQtyMatch && isProductType;
    });

    // Logic: Duyệt qua tất cả sales hợp lệ để tìm ra sale giảm sâu nhất
    applicableSales.forEach((sale) => {
      let currentFinalPrice = basePrice;

      switch (sale.sale_value_type) {
        case SALE_VALUE_TYPE_ENUM.VND:
          currentFinalPrice = sale.sale_value
            ? basePrice - sale.sale_value
            : sale.new_price_temp ?? basePrice;
          break;

        case SALE_VALUE_TYPE_ENUM.PERCENT:
          if (sale.sale_value) {
            currentFinalPrice = basePrice * (1 - sale.sale_value / 100);
          }
          break;

        default:
          // Trường hợp giá cố định (New Price)
          if (sale.new_price_temp != null && sale.new_price_temp > 0) {
            // Giữ nguyên giá topping không tham gia sale (nếu có)
            const toppingTotal = (item.moreTopping ?? []).reduce(
              (sum, t) => (t.can_sale ? sum + t.price : sum),
              0
            );
            currentFinalPrice = sale.new_price_temp + toppingTotal;
          }
          break;
      }

      currentFinalPrice = Math.max(currentFinalPrice, 0);

      // Nếu sale này cho giá tốt hơn (nhỏ hơn) thì cập nhật
      if (currentFinalPrice < bestFinalPrice) {
        bestFinalPrice = currentFinalPrice;
        appliedSale = sale;
      }
    });

    // Cộng dồn vào tổng tiền cơ sở sau sale sản phẩm
    baseTotal += bestFinalPrice * item.quantity;

    return {
      ...item,
      finalPrice: bestFinalPrice,
      appliedSale: appliedSale,
    };
  });

  return { items, baseTotal };
};

const applyCupSales = (
  orderList: OrderItem[],
  items: OrderItemWithFinalPrice[],
  productSales: SaleInfoResponse[],
  currentTotal: number
): { total: number; appliedSales: SaleInfoResponse[]; salesWithDiscount: SaleWithDiscount[] } => {
  let total = currentTotal;
  const appliedSales: SaleInfoResponse[] = [];
  const salesWithDiscount: SaleWithDiscount[] = [];

  for (const sale of productSales) {
    if (sale.sale_value_type !== SALE_VALUE_TYPE_ENUM.CUP) continue;
    if (!sale.sale_order_condition || !sale.sale_value) continue;

    const eligibleItems = sale.apply_for?.includes(ALL_ITEM_ID)
      ? orderList
      : orderList.filter(
          (item) =>
            sale.apply_for?.includes(item.id) &&
            (!sale.apply_for_size || sale.apply_for_size.includes(item.size))
        );

    const totalQty = eligibleItems.reduce((sum, item) => sum + item.quantity, 0);

    if (totalQty >= sale.sale_order_condition) {
      const promoTimes = Math.floor(totalQty / sale.sale_order_condition);
      const itemsExpanded = eligibleItems
        .flatMap((item) => Array(item.quantity).fill(item.price))
        .sort((a, b) => a - b);

      const discountItems = itemsExpanded.slice(0, promoTimes * sale.sale_value);
      const discountAmount = discountItems.reduce((sum, price) => sum + price, 0);

      total = Math.max(total - discountAmount, 0);
      appliedSales.push(sale);
      salesWithDiscount.push({
        sale,
        discountAmount,
        discountPercent: (discountAmount / currentTotal) * 100,
      });
    }
  }

  return { total, appliedSales, salesWithDiscount };
};

const applyOrderSales = (
  orderSales: SaleInfoResponse[],
  currentTotal: number
): { total: number; appliedSales: SaleInfoResponse[]; salesWithDiscount: SaleWithDiscount[] } => {
  let total = currentTotal;
  const appliedSales: SaleInfoResponse[] = [];
  const salesWithDiscount: SaleWithDiscount[] = [];

  for (const sale of orderSales) {
    if (sale.sale_order_condition && total < sale.sale_order_condition) continue;
    if (sale.sale_value == null) continue;

    let discountAmount = 0;

    switch (sale.sale_value_type) {
      case SALE_VALUE_TYPE_ENUM.VND:
        discountAmount = sale.sale_value;
        break;
      case SALE_VALUE_TYPE_ENUM.PERCENT:
        discountAmount = total * (sale.sale_value / 100);
        break;
    }

    if (sale.max_discount_value && sale.max_discount_value > 0) {
      discountAmount = Math.min(discountAmount, sale.max_discount_value);
    }

    total = Math.max(total - discountAmount, 0);
    appliedSales.push(sale);
    salesWithDiscount.push({
      sale,
      discountAmount,
      discountPercent: (discountAmount / currentTotal) * 100,
    });
  }

  return { total, appliedSales, salesWithDiscount };
};

const applyComboSales = (
  comboSales: SaleInfoResponse[],
  items: OrderItemWithFinalPrice[],
  orderList: OrderItem[],
  currentTotal: number
): { total: number; appliedSales: SaleInfoResponse[]; salesWithDiscount: SaleWithDiscount[] } => {
  let total = currentTotal;
  const appliedSales: SaleInfoResponse[] = [];
  const salesWithDiscount: SaleWithDiscount[] = [];

  for (const sale of comboSales) {
    if (!sale.combo_items?.length || !sale.sale_value) continue;

    if (sale.sale_order_condition && sale.sale_order_condition > 0) {
      // Combo n sản phẩm bất kỳ
      const allEligibleItems = items.filter((o) =>
        sale.combo_items?.some((c) => c.item_id === o.id && c.item_size === o.size)
      );

      const totalQty = allEligibleItems.reduce((sum, o) => sum + o.quantity, 0);

      if (totalQty >= sale.sale_order_condition) {
        const promoTimes = Math.floor(totalQty / sale.sale_order_condition);

        const expandedItems = allEligibleItems
          .flatMap((o) => Array(o.quantity).fill({ price: o.finalPrice, ref: o }))
          .sort((a, b) => a.price - b.price);

        const usedItems = expandedItems.slice(0, promoTimes * sale.sale_order_condition);
        const comboOriginalTotal = usedItems.reduce((sum, u) => sum + u.price, 0);

        const discountAmount = comboOriginalTotal - sale.sale_value * promoTimes;
        total = Math.max(total - discountAmount, 0);
        appliedSales.push(sale);
        salesWithDiscount.push({
          sale,
          discountAmount,
          discountPercent: (discountAmount / currentTotal) * 100,
        });
      }
    } else {
      // Combo cụ thể
      let comboQty = Infinity;

      for (const comboItem of sale.combo_items) {
        const orderedQty = orderList
          .filter((o) => o.id === comboItem.item_id && o.size === comboItem.item_size)
          .reduce((sum, o) => sum + o.quantity, 0);
        comboQty = Math.min(comboQty, orderedQty);
      }

      if (comboQty > 0 && comboQty !== Infinity) {
        let comboOriginalTotal = 0;

        for (const comboItem of sale.combo_items) {
          const orderedItems = items.filter(
            (o) => o.id === comboItem.item_id && o.size === comboItem.item_size
          );

          let qtyRemaining = comboQty;
          for (const o of orderedItems) {
            if (qtyRemaining <= 0) break;
            const qtyUsed = Math.min(o.quantity, qtyRemaining);
            comboOriginalTotal += o.finalPrice * qtyUsed;
            qtyRemaining -= qtyUsed;
          }
        }

        const discountAmount = comboOriginalTotal - sale.sale_value * comboQty;
        total = Math.max(total - discountAmount, 0);
        appliedSales.push(sale);
        salesWithDiscount.push({
          sale,
          discountAmount,
          discountPercent: (discountAmount / currentTotal) * 100,
        });
      }
    }
  }

  return { total, appliedSales, salesWithDiscount };
};

const applyBestProductSaleToItems = (
  orderList: OrderItem[],
  bestSales: SaleInfoResponse[],
  quantityMap: Map<string, number>
): OrderItemWithFinalPrice[] => {
  return orderList.map((item) => {
    const basePrice = calculateBasePrice(item);
    const key = `${item.id}_${item.size}`;
    const totalQty = quantityMap.get(key) ?? item.quantity;

    let finalPrice = basePrice;
    let appliedSale: SaleInfoResponse | undefined;

    for (const sale of bestSales) {
      // ✅ CHỈ xử lý product sale
      if (sale.sale_type !== SALE_TYPE_ENUM.EACH_ITEM) continue;

      // ❌ CUP xử lý ở tầng khác
      if (sale.sale_value_type === SALE_VALUE_TYPE_ENUM.CUP) continue;

      // Check apply_for
      if (!sale.apply_for?.includes(ALL_ITEM_ID) && !sale.apply_for?.includes(item.id)) continue;

      // Check size
      if (sale.apply_for_size && !sale.apply_for_size.includes(item.size)) continue;

      // Check quantity condition
      if (sale.sale_order_condition && totalQty < sale.sale_order_condition) continue;

      appliedSale = sale;

      switch (sale.sale_value_type) {
        case SALE_VALUE_TYPE_ENUM.VND:
          finalPrice = sale.sale_value ? basePrice - sale.sale_value : basePrice;
          break;

        case SALE_VALUE_TYPE_ENUM.PERCENT:
          finalPrice = sale.sale_value ? basePrice * (1 - sale.sale_value / 100) : basePrice;
          break;
      }

      break; // ❗ chỉ cho phép 1 product sale
    }

    return {
      ...item,
      finalPrice: Math.max(finalPrice, 0),
      appliedSale,
    };
  });
};

// ============ MAIN CALCULATION FUNCTION ============

export function calculateOrderPrice(
  orderList: OrderItem[],
  saleActive: SaleInfoResponse[],
  options: CalculateOrderOptions = {}
): OrderCalculationResult {
  const { useNewUserSale = false, totalOrders = 0 } = options;
  const now = Date.now();

  const newUserSales = filterActiveSales(saleActive, SALE_TYPE_ENUM.NEW_USER, now, totalOrders);
  const productSales = filterActiveSales(saleActive, SALE_TYPE_ENUM.EACH_ITEM, now);
  const orderSales = filterActiveSales(saleActive, SALE_TYPE_ENUM.ALL_BILL, now);
  const comboSales = filterActiveSales(saleActive, SALE_TYPE_ENUM.COMBO, now);

  const quantityMap = buildQuantityMap(orderList);
  const originalTotal = orderList.reduce(
    (sum, item) => sum + calculateBasePrice(item) * item.quantity,
    0
  );

  // BƯỚC 1: Áp dụng Product Sales (VND, PERCENT, NEW_PRICE)
  // Đây là nền tảng cho baseTotal
  const { items: itemsWithProductSale, baseTotal } = applyProductSales(
    orderList,
    productSales,
    quantityMap
  );

  // BƯỚC 2: Tính toán các Sale đặc biệt để đưa vào bestSale/allSales
  const productSalesDiscounts: SaleWithDiscount[] = [];
  const appliedProductSalesMap = new Map<number, { sale: SaleInfoResponse; amount: number }>();

  itemsWithProductSale.forEach((item) => {
    if (item.appliedSale) {
      const discount = (calculateBasePrice(item) - item.finalPrice) * item.quantity;
      if (discount > 0) {
        const curr = appliedProductSalesMap.get(item.appliedSale.id) || {
          sale: item.appliedSale,
          amount: 0,
        };
        appliedProductSalesMap.set(item.appliedSale.id, {
          ...curr,
          amount: curr.amount + discount,
        });
      }
    }
  });

  appliedProductSalesMap.forEach((val) => {
    productSalesDiscounts.push({
      sale: val.sale,
      discountAmount: val.amount,
      discountPercent: (val.amount / originalTotal) * 100,
    });
  });

  // BƯỚC 3: Chạy Scenario
  const scenarios: SaleScenario[] = [];

  // Scenario 1: Product + Cup + Order + Combo
  let s1Total = baseTotal;
  let s1Sales: SaleInfoResponse[] = Array.from(appliedProductSalesMap.values()).map((v) => v.sale);
  let s1SalesWithDiscount: SaleWithDiscount[] = [...productSalesDiscounts];

  // A. Cup Sales (Như ID 19, 17 của bạn)
  const cupResult = applyCupSales(orderList, itemsWithProductSale, productSales, s1Total);
  s1Total = cupResult.total;
  s1Sales.push(...cupResult.appliedSales);
  s1SalesWithDiscount.push(...cupResult.salesWithDiscount);

  // B. Order Sales
  const orderResult = applyOrderSales(orderSales, s1Total);
  s1Total = orderResult.total;
  s1Sales.push(...orderResult.appliedSales);
  s1SalesWithDiscount.push(...orderResult.salesWithDiscount);

  // C. Combo Sales
  const comboResult = applyComboSales(comboSales, itemsWithProductSale, orderList, s1Total);
  s1Total = comboResult.total;
  s1Sales.push(...comboResult.appliedSales);
  s1SalesWithDiscount.push(...comboResult.salesWithDiscount);

  scenarios.push({
    items: itemsWithProductSale,
    discountedTotal: s1Total,
    appliedSales: s1Sales,
    totalDiscount: originalTotal - s1Total,
    salesWithDiscount: s1SalesWithDiscount,
  });

  // Scenario 2: New User
  if (useNewUserSale && newUserSales.length > 0) {
    let s2Total = s1Total;
    const nuRes = applyOrderSales(newUserSales, s2Total);
    scenarios.push({
      items: itemsWithProductSale,
      discountedTotal: nuRes.total,
      appliedSales: [...s1Sales, ...nuRes.appliedSales],
      totalDiscount: originalTotal - nuRes.total,
      salesWithDiscount: [...s1SalesWithDiscount, ...nuRes.salesWithDiscount],
    });
  }

  const bestScenario = scenarios.reduce((b, c) => (c.totalDiscount > b.totalDiscount ? c : b));

  // BƯỚC 4: Xử lý hiển thị appliedSale cho Cup Sale (ID 19)
  // Nếu Scenario tốt nhất có dùng Cup Sale, ta gán nó vào item để UI hiển thị tên sale
  const cupSaleApplied = bestScenario.appliedSales.find(
    (s) => s.sale_value_type === SALE_VALUE_TYPE_ENUM.CUP
  );
  const finalItems = bestScenario.items.map((item) => {
    // Nếu món này chưa có sale sản phẩm và thỏa mãn điều kiện của Cup Sale đang áp dụng
    if (!item.appliedSale && cupSaleApplied) {
      const isMatch =
        cupSaleApplied.apply_for?.includes(ALL_ITEM_ID) ||
        cupSaleApplied.apply_for?.includes(item.id);
      if (isMatch) return { ...item, appliedSale: cupSaleApplied };
    }
    return item;
  });

  const bestSale =
    bestScenario.salesWithDiscount.length > 0
      ? bestScenario.salesWithDiscount.reduce((b, c) =>
          c.discountAmount > b.discountAmount ? c : b
        )
      : undefined;

  return {
    items: finalItems,
    originalTotal,
    discountedTotal: Math.max(0, bestScenario.discountedTotal),
    totalDiscount: originalTotal - bestScenario.discountedTotal,
    appliedOrderSale: bestScenario.appliedSales.filter(
      (s) => s.sale_type === SALE_TYPE_ENUM.ALL_BILL || s.sale_type === SALE_TYPE_ENUM.NEW_USER
    ),
    appliedAllSales: bestScenario.appliedSales,
    bestSale,
    allSalesWithDiscount: bestScenario.salesWithDiscount.sort(
      (a, b) => b.discountAmount - a.discountAmount
    ),
  };
}
